import { FC, ReactNode } from 'react';

import { HoleInOneClaimDetails } from '@/api/holeInOneClaim/holeInOneClaim.types.ts';
import CustomGrid from '@/components/controls/CustomGrid.tsx';
import CustomGridLabelValueRow, {
  LabelValueGridRow,
} from '@/components/controls/CustomGridLabelValueRow.tsx';
import { transformUnixToDateWithFormat } from '@/utils/dateUtil.ts';
import { capitalizeText } from '@/utils/valueFormatter.ts';

type HoleInOneClaimDetailsGridProps = {
  claimDetails: HoleInOneClaimDetails;
};

const HoleInOneClaimDetailsGrid: FC<HoleInOneClaimDetailsGridProps> = ({
  claimDetails,
}: HoleInOneClaimDetailsGridProps) => {
  const gridData: LabelValueGridRow[] = [
    {
      id: 'golferName',
      rowStyle: 'col-span-1 flex flex-col sm:flex-row gap-1 sm:gap-4',
      label: "Golfer's Name:",
      labelStyle: 'w-full sm:w-1/3 md:w-1/4 font-semibold sm:font-normal',
      value: claimDetails.submittedBy,
      valueStyle: 'w-full sm:w-2/3 md:w-3/4',
    },
    {
      id: 'courseName',
      rowStyle: 'col-span-1 flex flex-col sm:flex-row gap-1 sm:gap-4',
      label: 'Course Name:',
      labelStyle: 'w-full sm:w-1/3 md:w-1/4 font-semibold sm:font-normal',
      value: claimDetails.golfCourseName,
      valueStyle: 'w-full sm:w-2/3 md:w-3/4',
    },
    {
      id: 'holeNumber',
      rowStyle: 'col-span-1 flex flex-col sm:flex-row gap-1 sm:gap-4',
      label: 'Hole Number:',
      labelStyle: 'w-full sm:w-1/3 md:w-1/4 font-semibold sm:font-normal',
      value: claimDetails.holeNumber,
      valueStyle: 'w-full sm:w-2/3 md:w-3/4',
    },
    {
      id: 'registerTime',
      rowStyle: 'col-span-1 flex flex-col sm:flex-row gap-1 sm:gap-4',
      label: 'Hole-in-One Register Time:',
      labelStyle: 'w-full sm:w-1/3 md:w-1/4 font-semibold sm:font-normal',
      value: claimDetails.happenedAtUnix,
      transform: transformUnixToDateWithFormat,
      valueStyle: 'w-full sm:w-2/3 md:w-3/4',
    },
    {
      id: 'rewardTier',
      rowStyle: 'col-span-1 flex flex-col sm:flex-row gap-1 sm:gap-4',
      label: 'Reward Tier:',
      labelStyle: 'w-full sm:w-1/3 md:w-1/4 font-semibold sm:font-normal',
      value: claimDetails.rewardTierName,
      valueStyle: 'w-full sm:w-2/3 md:w-3/4',
    },
    {
      id: 'roundDateAndTime',
      rowStyle: 'col-span-1 flex flex-col sm:flex-row gap-1 sm:gap-4',
      label: 'Round Date & Time:',
      labelStyle: 'w-full sm:w-1/3 md:w-1/4 font-semibold sm:font-normal',
      value: claimDetails.roundStartedAtUnix,
      transform: transformUnixToDateWithFormat,
      valueStyle: 'w-full sm:w-2/3 md:w-3/4',
    },
    {
      id: 'insuranceClaimNumber',
      rowStyle: 'col-span-1 flex flex-col sm:flex-row gap-1 sm:gap-4',
      label: 'Insurance Claim Number:',
      labelStyle: 'w-full sm:w-1/3 md:w-1/4 font-semibold sm:font-normal',
      value: claimDetails.insuranceClaimNumber,
      noValueText: `No insurance number provided for the ${claimDetails.status} Hole-in-One claim.`,
      valueStyle: 'w-full sm:w-2/3 md:w-3/4',
    },
    {
      id: 'insuranceProvider',
      rowStyle: 'col-span-1 flex flex-col sm:flex-row gap-1 sm:gap-4',
      label: 'Insurance Provider:',
      labelStyle: 'w-full sm:w-1/3 md:w-1/4 font-semibold sm:font-normal',
      value: claimDetails.insuranceProvider,
      noValueText: `No insurance provider is specified for the ${claimDetails.status} Hole-in-One claim.`,
      valueStyle: 'w-full sm:w-2/3 md:w-3/4',
    },
    {
      id: 'status',
      rowStyle:
        'col-span-1 flex flex-col sm:flex-row gap-1 sm:gap-4 mt-2 sm:mt-0',
      label: 'Status:',
      labelStyle:
        'w-full sm:w-1/3 md:w-1/4 font-semibold sm:font-normal self-center sm:self-start',
      value: claimDetails.status,
      transform: capitalizeText,
      valueStyle: 'w-full sm:w-2/3 md:w-3/4 text-lg font-bold',
    },
  ];

  const holeInOneClaimDetailsGridItem: ReactNode[] = gridData.map((item) => (
    <CustomGridLabelValueRow
      key={item.id}
      id={item.id}
      rowStyle={item.rowStyle}
      label={item.label}
      labelStyle={item.labelStyle}
      value={item.transform ? item.transform(item.value) : item.value}
      noValueText={item.noValueText}
      valueStyle={item.valueStyle}
    />
  ));

  return (
    <CustomGrid
      items={holeInOneClaimDetailsGridItem}
      cols={1}
      gap={4}
      className="mt-2 sm:mt-4 mb-4"
    />
  );
};

export default HoleInOneClaimDetailsGrid;
